import { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const TOAST_OPTS = {
  position: toast.POSITION.TOP_CENTER,
  draggable: false,
  closeOnClick: false,
  autoClose: 5000,
  hideProgressBar: true,
};

export default function usePermissionSet() {
  const [permissionSet, setPermissionSet] = useState({});
  const [onSaved, setOnSaved] = useState(() => () => {});

  const permissionSetChanged = useCallback((ps) => setPermissionSet(ps || {}), []);

  const fetchPermissionSet = useCallback((team, toFetch, onPermissionSetSaved) => {
    setOnSaved(() => onPermissionSetSaved || (() => {}));
    const endpoint =
      toFetch && toFetch.id
        ? `/teams/${team.id}/permission_sets/${toFetch.id}/edit`
        : `/teams/${team.id}/permission_sets/new`;
    return axios.get(endpoint).then(({ data }) => {
      setPermissionSet(Object.assign({}, data, toFetch));
    });
  }, []);

  const selectAllPermissionRulesClicked = useCallback((checked) => {
    setPermissionSet((ps) =>
      Object.assign({}, ps, {
        permission_rules: (ps.permission_rules || []).map((pr) =>
          Object.assign({}, pr, { enabled: checked })
        ),
      })
    );
  }, []);

  const permissionRuleEnabledClicked = useCallback((permissionRule) => {
    const isEnabled = !permissionRule.enabled;
    setPermissionSet((ps) =>
      Object.assign({}, ps, {
        permission_rules: (ps.permission_rules || []).map((pr) => {
          const isMatch =
            (permissionRule.rulable_type === pr.rulable_type &&
              permissionRule.rulable_id === pr.rulable_id) ||
            (permissionRule.rulable_type === "Role" &&
              pr.rulable_type === "TeamMembership" &&
              pr.rulable_data.role_id === permissionRule.rulable_id);
          return isMatch ? Object.assign({}, pr, { enabled: isEnabled }) : pr;
        }),
      })
    );
  }, []);

  const submitPermissionSet = useCallback((csrfToken, team, ps, onPermissionSetSaved) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    const updating = ps && ps.id;
    const method = updating ? "patch" : "post";
    const url = updating
      ? `/teams/${team.id}/permission_sets/${ps.id}`
      : `/teams/${team.id}/permission_sets`;
    const permission_rules_attributes = ps.permission_rules.map((pr) => ({
      id: pr.id,
      enabled: pr.enabled,
      rulable_type: pr.rulable_type,
      rulable_id: pr.rulable_id,
    }));
    return axios
      .request({
        method,
        url,
        data: {
          permission_set: {
            element_id: ps.element_id,
            element_type: ps.element_type,
            permission_rules_attributes,
          },
        },
      })
      .then(({ data }) => {
        onPermissionSetSaved(data);
        setPermissionSet({});
        toast.success("Permissions saved", TOAST_OPTS);
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), TOAST_OPTS);
      });
  }, []);

  return {
    permissionSet,
    onPermissionSetSaved: onSaved,
    permissionSetChanged,
    fetchPermissionSet,
    selectAllPermissionRulesClicked,
    permissionRuleEnabledClicked,
    submitPermissionSet,
  };
}
